import ensureError from "@-/com.npmjs.ensure-error";
import { type SuperwallEventInfo, useSuperwallEvents, useUser } from "@-/com.npmjs.expo-superwall";
import {
  captureAnalyticsEvent,
  useAnalyticsDistinctId,
} from "@-/dev.improve.haniverse.lib.analytics";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useCallback, useEffect } from "react";
import { mapSuperwallAnalyticsEvent } from "#superwall-analytics-events";

/**
 * Forwards Superwall's paywall and transaction events to PostHog, and tells Superwall which
 * PostHog person this device is, so the server-side `sw_*` subscription events (trial starts,
 * conversions, renewals, refunds, revenue) join the same person as the in-app funnel.
 *
 * The link is re-sent on every identity change, which also covers unverified local accounts:
 * they are never passed to Superwall `identify`, so their purchases would otherwise carry only
 * Superwall's anonymous alias. If Superwall's PostHog integration turns out to ignore
 * `posthogUserId`, the fallback is `posthog.alias(<Superwall aliasId>)`, issued only while the
 * PostHog distinct id is still anonymous so two accounts can never be merged.
 *
 * Mount only where Superwall is configured: `setIntegrationAttributes` dereferences the native
 * `Superwall.shared`, which does not exist on keyless builds.
 */
export function SuperwallAnalyticsBridge() {
  const onSuperwallEvent = useCallback((eventInfo: SuperwallEventInfo) => {
    try {
      const mapped = mapSuperwallAnalyticsEvent(eventInfo);
      if (mapped !== null) {
        captureAnalyticsEvent(mapped.name, mapped.properties);
      }
    } catch (error) {
      logger(import.meta.location).warn("Superwall analytics event mapping failed", error);
    }
  }, []);
  useSuperwallEvents({ onSuperwallEvent });

  const distinctId = useAnalyticsDistinctId();
  const { setIntegrationAttributes } = useUser();
  useEffect(() => {
    if (distinctId === null) {
      return;
    }
    void setIntegrationAttributes({ posthogUserId: distinctId }).catch((error: unknown) => {
      logger(import.meta.location).warn(
        "Superwall PostHog integration attribute failed",
        ensureError(error),
      );
    });
  }, [distinctId, setIntegrationAttributes]);

  return null;
}
